import DOMPurify from 'dompurify';

/**
 * Sanitizes untrusted HTML content to prevent Cross-Site Scripting (XSS) attacks.
 * * This helper addresses audit finding NOTICE_2.01 by ensuring that user-supplied
 * or dynamic data is cleaned before being rendered in the DOM.
 * It helps maintain compliance with PCI-DSS 4.0.1 (Requirements 6.2 and 6.4)
 * and follows OWASP Top Ten best practices for Injection prevention.
 *
 * @param {string} txt - The raw HTML string to be sanitized.
 * @returns {string} - The sanitized, safe-to-render HTML string.
 */
export const sanitizeContent = function (txt) {
  return !!txt ? DOMPurify.sanitize(txt, {ADD_TAGS: ['use']}) : '';
};

/**
 * Recursively traverses an object or array to sanitize all string values.
 * This directly addresses finding NOTICE_2.01 from the IT Security Audit[cite: 22].
 * It ensures compliance with PCI-DSS 4.0.1 (Requirements 6.2 and 6.4)
 * by encoding/sanitizing potential XSS vectors in data structures.
 * * @param {any} data - The object, array, or string to be sanitized.
 * @returns {any} - The sanitized data structure.
 */
export const sanitizeData = (data) => {
  if (typeof data === 'string') {
    return DOMPurify.sanitize(data);
  }

  if (Array.isArray(data)) {
    return data.map((item) => sanitizeData(item));
  }

  if (data !== null && typeof data === 'object') {
    const sanitizedObj = {};
    Object.keys(data).forEach((key) => {
      sanitizedObj[key] = sanitizeData(data[key]);
    });
    return sanitizedObj;
  }

  return data;
};
